import type { CalculatedAttributeResult } from '@hyphengroup/cms-components/calculatedAttributes/resolve';
import type { AttrNumber } from '@hyphengroup/cms-components/types/attributes';
import {
  ATTR_NAMES,
  getAttrFromProduct,
} from '@hyphengroup/cms-components/types/attrNames';
import { CmsProduct } from '@hyphengroup/cms-components/types/cmsProduct';
import { formatAttributeItemValue } from '@hyphengroup/cms-components/utils/helpers/attrHelpers';

/**
 * Only an address containing "@" counts as usable (CMS-1818 §5). The field is
 * deliberately not validated in the UI, so anything else — empty, or plain text
 * — means "submitted, no email": no lead, no PII in tracking.
 */
export const hasUsableEmail = (email: string): boolean => email.includes('@');

/**
 * Monthly repayment for the summary row, formatted as on the card the user came
 * from, behind the market currency (the attribute's own Strapi prefix is
 * skipped, like the rest of the summary line).
 *
 * `calculated` is the engine's answer in the opening block's slot. A block on
 * the engine answers for itself — its figure, or none while it is empty or
 * pending. `undefined` means the engine does not calculate there: a legacy
 * block has rewritten the attribute for the filter's loan (`'10,162'`, or the
 * raw Strapi `'8493'`), and that is the figure.
 *
 * No value, or a zero one — as products carry it until a recalculation lands —
 * means there is nothing to show, and the line renders without the repayment.
 */
export const getMonthlyRepayment = (
  product: CmsProduct | undefined,
  currencyDisplay: string,
  calculated: CalculatedAttributeResult | undefined,
): string | undefined => {
  const attr = getAttrFromProduct(ATTR_NAMES.MONTHLY_REPAYMENT, product);
  const value =
    calculated?.provider === 'PL_CALCULATOR'
      ? calculated.state === 'resolved' && calculated.value.raw !== 0
        ? String(Math.round(calculated.value.raw))
        : undefined
      : attr?.value && Number(attr.value) !== 0
        ? attr.value
        : undefined;
  if (!value) return undefined;

  // One formatter for both paths, so a product without the attribute still
  // prints the engine's figure grouped.
  const shape: AttrNumber = attr ?? {
    __typename: 'ComponentAttributesAttrNumber',
  };
  return `${currencyDisplay}${formatAttributeItemValue({ ...shape, value }, false, true, false)}`;
};
